import type { AxiosRequestConfig } from 'axios';
import { getDigitalOceanSshKeys } from '@/api/digitalocean';
import { getHetznerSshKeys, type SshKeyDto } from '@/api/hetzner';

/** The API's provider ids; requests and runners without one are Hetzner's. */
export type CloudProvider = 'hetzner' | 'digitalocean';

export const CLOUD_PROVIDERS: CloudProvider[] = ['hetzner', 'digitalocean'];

/** Products name their provider "Hetzner" or "DigitalOcean", runners and drafts "digitalocean" or nothing. */
export function cloudProvider(value: string | null | undefined): CloudProvider {
  return value?.toLowerCase() === 'digitalocean' ? 'digitalocean' : 'hetzner';
}

/** The backend lists DigitalOcean products only while DigitalOcean is switched on for this environment. */
export function offersDigitalOcean(products: { provider?: string | null }[]): boolean {
  return products.some((product) => cloudProvider(product.provider) === 'digitalocean');
}

export function productsOf<T extends { provider?: string | null }>(
  products: T[],
  provider: CloudProvider
): T[] {
  return products.filter((product) => cloudProvider(product.provider) === provider);
}

export const DIGITALOCEAN_TOKEN_PAGE = 'https://cloud.digitalocean.com/account/api/tokens';

export const DIGITALOCEAN_TOKEN_SCOPES = [
  'account:read',
  'droplet:create',
  'droplet:read',
  'droplet:update',
  'droplet:delete',
  'image:read',
  'reserved_ip:create',
  'reserved_ip:read',
  'reserved_ip:update',
  'reserved_ip:delete',
  'ssh_key:read',
  'tag:create',
];

const DIGITALOCEAN_TOKEN_HELP =
  'A DigitalOcean personal access token with the custom scopes ' +
  `${DIGITALOCEAN_TOKEN_SCOPES.join(', ')} (Control Panel → API → Tokens → Generate New Token → Custom Scopes).`;

export interface ProviderTexts {
  name: string;
  /** Where a runner lives and is billed: Hetzner's project, DigitalOcean's account. */
  container: string;
  tokenLabel: string;
  tokenPlaceholder: string;
  tokenRequired: string;
  tokenHelp: string;
  sshKeysLabel: string;
  sshKeysEmpty: string;
  /** Prefix of a monthly price's tooltip. */
  priceSource: string;
  /** Why a product cannot be duplicated or resumed in its location. */
  unavailable: string;
}

export const PROVIDER_TEXTS: Record<CloudProvider, ProviderTexts> = {
  hetzner: {
    name: 'Hetzner',
    container: 'project',
    tokenLabel: 'Hetzner Auth Token',
    tokenPlaceholder: 'Hetzner API token',
    tokenRequired: 'Hetzner auth token is required.',
    tokenHelp:
      'Read + Write Token from the Hetzner project. We recommend a separate project that only ' +
      'contains your runners. If there is an SSH key in your Hetzner project, it will be used as root key.',
    sshKeysLabel: 'Hetzner SSH Keys',
    sshKeysEmpty: 'No SSH keys found for this Hetzner token.',
    priceSource: 'Hetzner Cloud',
    unavailable: 'Hetzner reports this server type as unavailable in this location.',
  },
  digitalocean: {
    name: 'DigitalOcean',
    container: 'account',
    tokenLabel: 'DigitalOcean API Token',
    tokenPlaceholder: 'DigitalOcean API token',
    tokenRequired: 'DigitalOcean API token is required.',
    tokenHelp:
      `${DIGITALOCEAN_TOKEN_HELP} Runners are created in and billed to the token's account; ` +
      'the SSH keys of the account (Settings → Security) can be added below.',
    sshKeysLabel: 'DigitalOcean SSH Keys',
    sshKeysEmpty: 'No SSH keys found for this DigitalOcean token.',
    priceSource: 'DigitalOcean',
    unavailable: 'DigitalOcean reports this droplet size as unavailable in this location.',
  },
};

export function providerTexts(provider: CloudProvider): ProviderTexts {
  return PROVIDER_TEXTS[provider];
}

/** Shown where a DigitalOcean runner is created; its costs differ from Hetzner's. */
export const DIGITALOCEAN_COSTS_NOTE =
  'Droplets are billed per second, at least 60 seconds. Each runner keeps a reserved IPv4 and IPv6 ' +
  'address in its region, also while paused; a paused runner’s reserved IPv4 costs USD 5 per month. ' +
  'New accounts may reserve only 3 IP addresses and run few droplets: ask DigitalOcean to raise these limits.';

/** Burst runners are never paused, so their reserved IPs go with them. */
export const DIGITALOCEAN_BURST_COSTS_NOTE =
  'Droplets are billed per second, at least 60 seconds. Each burst runner gets a reserved IPv4 and IPv6 ' +
  'address in its region, released when it is deleted. The account’s limits apply to burst runners too: ' +
  'new accounts may reserve only 3 IP addresses and run few droplets; ask DigitalOcean to raise these limits.';

export const DIGITALOCEAN_LABELS_NOTE = 'Labels are not attached to DigitalOcean droplets.';

/** The provider's SSH keys listed with a token that need not be stored yet. */
export function listProviderSshKeys(
  provider: CloudProvider,
  ...args: [token: string, config?: AxiosRequestConfig]
): Promise<SshKeyDto[]> {
  return provider === 'digitalocean' ? getDigitalOceanSshKeys(...args) : getHetznerSshKeys(...args);
}

/** A Hetzner request keeps its `hetznerAuthToken`; other providers send their id and `providerAuthToken`. */
export function providerTokenFields(
  provider: CloudProvider,
  token: string
): { hetznerAuthToken: string } | { provider: CloudProvider; providerAuthToken: string } {
  return provider === 'hetzner'
    ? { hetznerAuthToken: token }
    : { provider, providerAuthToken: token };
}
